import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { useBlog } from '../context/BlogContext';
import { useTheme } from '../context/ThemeContext';
import api from '../utils/api';
import { toast } from 'react-hot-toast';
import { generateBlogPostUrl } from '../utils/urlEncoder';

const BlogPosts = () => {
  const { darkMode } = useTheme();
  const [loading, setLoading] = useState(true); 
  const [posts, setPosts] = useState([]);
  const [imgLoading, setImgLoading] = useState([]); // Track image loading state for each post
  const { setCurrentPost } = useBlog();

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await api.get('/blog');
        
        if (response.data.success) {
          const fetchedPosts = response.data.posts.map(post => {
            // Handle both old and new blog post formats
            const getDescription = () => {
              if (post.descriptions && Array.isArray(post.descriptions) && post.descriptions.length > 0) {
                return post.descriptions[0].text || '';
              }
              return post.description || '';
            };

            const getContent = () => {
              if (post.contentSections && Array.isArray(post.contentSections) && post.contentSections.length > 0) {
                return post.contentSections.map(section => section.content).join('\n\n');
              }
              return post.content || '';
            };

            return {
              id: post._id,
              title: post.title,
              description: getDescription(),
              image: post.image || '',
              postname: post.authorName,
              authorImage: post.authorImage || 'https://avatars.githubusercontent.com/u/123456789',
              date: post.createdAt,
              readingTime: post.readingTime,
              category: post.category,
              content: getContent(),
              views: post.views,
              likes: post.likes,
              // Keep original data for compatibility
              descriptions: post.descriptions,
              contentSections: post.contentSections
            };
          });
          
          setPosts(fetchedPosts);
          setImgLoading(Array(fetchedPosts.length).fill(true));
        } else {
          // Fallback to empty array if no posts
          setPosts([]);
          setImgLoading([]);
        }
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        toast.error('Failed to load blog posts');
        // Set empty state on error
        setPosts([]);
        setImgLoading([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  const handleImageLoad = (index) => {
    setImgLoading((prev) => {
      const newLoading = [...prev];
      newLoading[index] = false; // Set specific image to loaded
      return newLoading;
    });
  };

  // Loading sketch placeholder
  const LoadingSketch = () => (
    <div className="max-w-6xl h-fit mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
      {[...Array(3)].map((_, index) => (
        <article key={index} className="flex flex-col md:flex-row gap-6 border-b border-gray-200 dark:border-gray-700 py-8 animate-pulse">
          <div className="w-full md:w-1/3 h-48 bg-gray-300 dark:bg-gray-700 rounded-lg"></div>
          <div className="w-full md:w-2/3 space-y-3">
            <div className="h-6 w-1/2 bg-gray-300 dark:bg-gray-600 rounded"></div>
            <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 w-1/3 bg-gray-200 dark:bg-gray-700 rounded mt-4"></div>
          </div>
        </article>
      ))}
    </div>
  );

  useEffect(() => {
    // Set page title
    document.title = "Tech News Blog | Latest in Web Development and Technology";
    
    // Set meta tags
    document.querySelector('meta[name="description"]').setAttribute(
      "content", 
      "Stay updated with the latest tech news, tutorials, and insights on web development, databases, frontend frameworks, and image management."
    );
    
    document.querySelector('meta[name="keywords"]').setAttribute(
      "content", 
      "tech news, web development, MongoDB, React, Vite, Tailwind CSS, Cloudinary, frontend, database, image management"
    );
  }, []);

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-white'} py-16 -mt-8`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <header className="text-center mb-12">
          <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-gray-50' : 'text-gray-900'}`}>
            Latest Articles
          </h1>
          <div className={`h-1 w-16 mx-auto mb-4 ${darkMode ? 'bg-gray-50' : 'bg-gray-900'}`}></div>
        </header>
        
        {loading ? (
          <LoadingSketch />
        ) : posts.length > 0 ? (
          <div className="space-y-6">
            {posts.map((post, index) => (
              <article 
                key={post.id}
                className={`group flex flex-col md:flex-row gap-6 h-fit lg:mb-8 mb-4 overflow  rounded-lg  hover:shadow-md transition-shadow p-4 shadow-lg ${darkMode ? 'bg-gray-900/25   ' : 'bg-white/25'}`}
              >
                <div className="w-full h-fit md:w-1/3  -hidden rounded-lg">
                  {imgLoading[index] && (
                    <div className="w-full h-48 bg-gray-200 dark:bg-gray-700 animate-pulse rounded-lg"></div>
                  )}
                  <img
                    src={post.image || 'https://via.placeholder.com/400x300?text=No+Image'}
                    alt={post.title}
                    className={`w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 ${
                      imgLoading[index] ? 'opacity-0 h-0' : 'opacity-100'
                    }`}
                    onLoad={() => handleImageLoad(index)}
                  />
                </div>
                
                <div className="w-full md:w-2/3 flex flex-col justify-between">
                  <div>
                    <h2 className="text-xl font-bold mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {post.title}
                    </h2>
                    <p className="mb-4 line-clamp-2">
                      {post.description}
                    </p>
                    <div className="flex flex-col mt-2">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(post.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                        <span className="mx-2">•</span>
                        <span>{post.postname}</span>
                      </div>
                      <Link
                        to={generateBlogPostUrl(post.id, post.title, post.postname, post.date)}
                        className="text-lg font-bold text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors duration-200"
                        onClick={() => setCurrentPost(post)}
                      >
                        Read more
                        <svg className="w-4 h-4 ml-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <h2 className="text-xl font-bold mb-2 text-gray-800 dark:text-gray-200">
              No articles found
            </h2>
            <p className={`text-gray-500 dark:text-gray-400`}>
              Check back later for new posts!
            </p>
          </div>
        )}
        

      </div>
      <Footer darkMode={darkMode} />
    </div>
  );
};

export default BlogPosts;
